import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useNavigate, useParams } from "react-router-dom";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import FolderTags from "../../components/BuilderComponents/FolderTags";
import NotesAndTags from "../../components/BuilderComponents/NotesAndTags";
import Button from "../../components/FormComponents/Button";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import { SCREEN_WIDTHS } from "../../constant/constant";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import { useAxiosContext } from "../../context/AxiosContext";
import { useScreenContext } from "../../context/ScreenContext";
import { useTreeContext } from "../../context/TreeDataContext";
import useFetchData from "../../hooks/useFetchData";
import useZTheme from "../../hooks/useZTheme";
import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import Loader from "../../Layouts/Loader/Loader";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import PageInfoAndActionBar from "../../Layouts/PageInfoAndActionBar";
import { updateScreenChecksum } from "../../utils/configChecksum/setterFunctions/screenConfig";
import { getInitialConfigOfScreen } from "../../utils/InitialRenderingData";
import { organizeScreenFolders } from "../../utils/screenutils";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import { getFolderFileById, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import { formatChartData } from "../ApplicationBlock/ChartRenderer/defaultChartTheme";
import PositionScreenWithEdit from "../FlexibleLayout/PositionScreenWithEdit";
import { convertGridToPixels, getScreenWidth } from "../FlexibleLayout/utils";
import CustomJsAndCssForScreen from "./CustomJsAndCssForScreen";
import "./Header.css";
import HeaderRendererDesktop from "./NewHeader/HeaderDesktopRenderer";
import NewHeaderRendererMobile from "./NewHeader/NewHeaderRendererMobile";
import NewScreenHeader from "./NewHeader/NewScreenHeader";
import ScreenElementsRightBar from "./ScreenElementsRightBar";
import ScreenGridLayout from "./ScreenGridLayout";
import ScreenOwnerAssignment from "./ScreenOwnerAssignment";
import axiosInstance, { handleApiError } from "../../services/axiosService";

const ScreenRenderer = () => {
  const initialLogoData = {
    logosrc: "",
    logosize: "",
    logotext: "",
    logotextsize: "",
    logotextweight: "",
    logotextcolor: "000000"
  };

  const [isLoading, setIsLoading] = useState(true);
  const [headerDataForPreview, setHeaderDataForPreview] = useState(null);

  const {
    setSelectedScreen,
    headerData,
    setLayouts,
    saveScreenJson,
    currentScreenData,
    layoutData,
    nestedGridData,
    setLayoutConfig,
    setCurrentScreenData
  } = useScreenContext();
  const { axiosInstance, handleError } = useAxiosContext();
  const { screensExplorerData } = useTreeContext();
  const { screenId } = useParams();

  const navigate = useNavigate();

  const fetchSingleScreen = async (id) => {
    try {
      setIsLoading(true);
      setLayouts([]);
      const { data } = await axiosInstance.get(`/screens/${id}`);
      updateScreenChecksum(data, data.name, true);
      const screenNewConfig = getInitialConfigOfScreen(data, screensExplorerData);
      const { layout, config } = screenNewConfig;
      const copyConfig = JSON.parse(JSON.stringify(config));
      if (copyConfig.name !== "header") {
        setLayouts(layout.layout);
        layoutData.current = layout.layoutData;
        nestedGridData.current = layout.nestedGridData;
        let newLayout = copyConfig.position_screen_config;
        if (copyConfig.device === "mobile") {
          const gridSize = data.position_screen_metadata?.grid_size || 4;
          newLayout = convertGridToPixels(copyConfig.position_screen_config || [], gridSize);
        }
        setLayoutConfig(newLayout || []);
      }
      const storeConfig = updateScreenChecksum(config, data.name);
      setCurrentScreenData({ ...copyConfig, ...storeConfig });
      setIsLoading(false);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchHeaderForPage = async () => {
    try {
      const screenId = headerData.current?.[currentScreenData.device];
      if (!screenId) {
        setHeaderDataForPreview(null);
        toastError("Header not found");
        return;
      }
      const { data } = await axiosInstance.get(`/screens/${screenId}`);
      const newHeaderConfig = data.header_config;
      newHeaderConfig.logo = newHeaderConfig.logo || initialLogoData;
      newHeaderConfig.menu = newHeaderConfig.menu || [];
      setHeaderDataForPreview(newHeaderConfig);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    if (currentScreenData.name === "header" || !currentScreenData.device) return;
    fetchHeaderForPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreenData.device, currentScreenData.id]);

  const initialRendering = async () => {
    if (!screensExplorerData || !screenId) return;
    const file = getFolderFileById(screensExplorerData, screenId);
    if (file) {
      setSelectedScreen({
        id: file.id,
        name: file.name,
        parentData: file.parentData,
        type: file.name === "header" ? "header" : "page"
      });

      await fetchSingleScreen(file.id);
    } else {
      navigate("../");
    }
  };
  useEffect(() => {
    if (screenId) {
      initialRendering();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenId]);

  return (
    <div className="content_main_section">
      {isLoading ? (
        <Loader />
      ) : (
        <div className="screen_outer_body">
          <>
            {currentScreenData.name === "header" ? (
              <NewScreenHeader currentScreenData={currentScreenData} saveScreenJson={saveScreenJson} />
            ) : (
              <DefaultScreenRenderer headerConfig={headerDataForPreview} />
            )}
          </>
        </div>
      )}
    </div>
  );
};

const DefaultScreenRenderer = ({ headerConfig }) => {
  const { currentScreenData } = useScreenContext();
  const { is_positioned_screen } = currentScreenData;
  return (
    <>
      {is_positioned_screen ? (
        <PositionedScreenComponent headerConfig={headerConfig} />
      ) : (
        <NonPositionedScreenComponent headerConfig={headerConfig} />
      )}
    </>
  );
};

const PositionedScreenComponent = ({ headerConfig }) => {
  const { currentScreenData, layoutConfig, setLayoutConfig } = useScreenContext();
  const { device } = currentScreenData;
  const { allApplicationBlockData, setAllApplicationBlockData } = useAppBlocksContext();
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [scaleFactor, setScaleFactor] = useState(1);
  useZTheme(device);

  // const [screenLayoutInfo, setScreenLayoutInfo] = useState({
  //   showGrid: true,
  //   showTree: false,
  //   showTemplate: false,
  //   screenMode: "editable",
  //   scaleFactor: 1,
  //   gridSize: (() => {
  //     if (device === "desktop") {
  //       return 8;
  //     } else {
  //       return currentScreenData.position_block_metadata?.grid_size || 4;
  //     }
  //   })()
  // });
  // const { showGrid, showTree, showTemplate, screenMode, gridSize, scaleFactor } = screenLayoutInfo;

  // const handleChangeLayoutInfo = (type, value) => {
  //   setScreenLayoutInfo({ ...screenLayoutInfo, [type]: value });
  // };

  return (
    <>
      <div className="screen-center-wrapper">
        <PageInfoAndActionBarForScreen />
        {/* <div className="page-action-bar-top"> */}
        {/* <PositionScreenActionBar
          screenLayoutInfo={screenLayoutInfo}
          setScreenLayoutInfo={setScreenLayoutInfo}
          device={device}
        /> */}
        {/* </div> */}
        <div className={`position-screen-center-page`} style={{ height: "calc(100vh - 92px)" }}>
          {headerConfig && (
            <div
              className={device === "desktop" ? "navbar_full_width" : ""}
              style={{
                zoom: scaleFactor,
                width: `${SCREEN_WIDTHS[device]}px`,
                margin: "auto"
              }}
            >
              {device === "desktop" || device === "tablet" ? (
                <HeaderRendererDesktop headerConfig={headerConfig} />
              ) : (
                <NewHeaderRendererMobile headerConfig={headerConfig} />
              )}
            </div>
          )}

          <PositionScreenWithEdit
            layoutConfig={layoutConfig || []}
            setLayoutConfig={setLayoutConfig}
            draggedItem={draggedItem}
            setDragOffset={setDragOffset}
            setDraggedItem={setDraggedItem}
            dragOffset={dragOffset}
            // type={showTemplate ? "view" : screenMode}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            // gridSize={gridSize}
            screenWidth={getScreenWidth({ device: device, metadata: currentScreenData.position_screen_metadata })}
            // showTree={showTree}
            // setShowTree={() => setScreenLayoutInfo({ ...screenLayoutInfo, showTree: false })}
            // showGrid={showGrid}
            tableId={currentScreenData.screen_settings?.table_id || ""}
            tableType={"statemachine"}
            device={device}
            // scaleFactor={scaleFactor}
            jobPlaces={"screen"}
            onScreenLayoutInfoChange={(layout) => setScaleFactor(layout.scaleFactor)}
          />
          {/* <BuilderModal
            isOpen={showTemplate}
            onClose={() => handleChangeLayoutInfo("showTemplate", false)}
            modalName="Templates"
            customStyleClsName="template_modal_fullsize"
            bodyStyle={{ minHeight: "100vh", overflow: "auto" }}
          >
            <PositionedTemplateList />
          </BuilderModal> */}
        </div>
      </div>
      <ScreenElementsRightBar
        allApplicationData={allApplicationBlockData}
        setAllApplicationData={setAllApplicationBlockData}
        setDragOffset={setDragOffset}
        setDraggedItem={setDraggedItem}
        device={device}
        isPositionScreen={true}
      />
    </>
  );
};

const NonPositionedScreenComponent = ({ headerConfig }) => {
  const [dragFromOutside, setDragFromOutside] = useState(false);
  const [allApplicationData, setAllApplicationData] = useState({});
  const { currentScreenData } = useScreenContext();
  const { device } = currentScreenData;
  return (
    <>
      <div className="screen-center-wrapper">
        <PageInfoAndActionBarForScreen />
        <div className="screens_page_outer page_main_section">
          <div
            className={`screen_header_draggable_outer  ${device === "mobile" ? "mobile-preview" : "desktop-preview"}`}
          >
            {headerConfig && (
              <>
                {device === "desktop" || device === "tablet" ? (
                  <div className="navbar_full_width">
                    <HeaderRendererDesktop headerConfig={headerConfig} />
                  </div>
                ) : (
                  <NewHeaderRendererMobile headerConfig={headerConfig} />
                )}
              </>
            )}

            <ScreenGridLayout
              dragFromOutside={dragFromOutside}
              setDragFromOutside={setDragFromOutside}
              allApplicationData={allApplicationData}
            />
          </div>
        </div>
      </div>
      <ScreenElementsRightBar
        allApplicationData={allApplicationData}
        setAllApplicationData={setAllApplicationData}
        setDragFromOutside={setDragFromOutside}
        device={device}
        isPositionScreen={false}
      />
    </>
  );
};

const PageInfoAndActionBarForScreen = () => {
  const { selectedScreen, layoutData, layouts, saveScreenJson, layoutConfig, currentScreenData, setCurrentScreenData } =
    useScreenContext();
  const { screensExplorerData, setScreensExplorerData } = useTreeContext();
  const [modalState, setModalState] = useState({
    settingsOpen: false,
    customJsOpen: false,
    notesOpen: false
  });

  const navigate = useNavigate();

  const handleCloneScreen = async () => {
    try {
      await saveScreenJson();
      const { data } = await axiosInstance.post(`screens/${selectedScreen.id}/clone`);
      const newItem = fileFolderConfig?.getFileConfig(data.id, data.name, data.device);
      const idObject = { id: data.device };
      const newTree = insertNewFileOrFolder(screensExplorerData, idObject, newItem);
      setScreensExplorerData(newTree);
      navigate(`../${data.id}`);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNotesAndTag = (data, keyName) => {
    setCurrentScreenData((prev) => ({
      ...prev,
      [keyName]: data
    }));
  };

  const handleFolderSave = async () => {
    try {
      const body = {
        folder_info_tag: currentScreenData.folder_info_tag || [],
        info_tag: currentScreenData.info_tag || [],
        description: currentScreenData.description || ""
      };

      const { message } = await axiosInstance.patch(`/screens/${selectedScreen.id}`, body);
      const { data: folderTagsData } = await axiosInstance.get("/folder-tag");

      const deviceIndex = currentScreenData.device === "desktop" ? 0 : 1;
      let updatedScreenItem;

      setScreensExplorerData((prevData) => {
        const deviceFolder = prevData[deviceIndex];

        let screenItem;
        deviceFolder.items.forEach((item) => {
          if (item.isFolder && item.items) {
            const found = item.items.find((subItem) => subItem.id === selectedScreen.id);
            if (found) screenItem = found;
          } else if (item.id === selectedScreen.id) {
            screenItem = item;
          }
        });

        if (screenItem) {
          updatedScreenItem = {
            ...screenItem,
            fileInfo: {
              ...screenItem.fileInfo,
              folder_info_tag: body.folder_info_tag
            }
          };
        }

        return organizeScreenFolders(
          prevData,
          updatedScreenItem,
          currentScreenData.device,
          body.folder_info_tag,
          folderTagsData,
          selectedScreen.id
        );
      });

      toastSuccess(message);
      setModalState({ ...modalState, notesOpen: false });
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <PageInfoAndActionBar
        pagePath={`Screens/${selectedScreen.name}`}
        clonePage={handleCloneScreen}
        savePage={saveScreenJson}
        settingPage={() => setModalState({ ...modalState, settingsOpen: true })}
        customFunc={() => setModalState({ ...modalState, customJsOpen: true })}
        currentConfig={[layouts, layoutData.current, layoutConfig]}
        notesPage={() => setModalState({ ...modalState, notesOpen: true })}
        pageName="screen"
      />
      {/* Settings Modal */}
      <BuilderRightSideBar
        isOpen={modalState.settingsOpen}
        onClose={() => setModalState({ ...modalState, settingsOpen: false })}
        heading="Settings"
        size="large"
      >
        <div className="p_1">
          <ScreenOwnerAssignment onClose={() => setModalState({ ...modalState, settingsOpen: false })} />
        </div>
      </BuilderRightSideBar>

      {/* Custom JS/CSS Modal */}
      <BuilderModal
        isOpen={modalState.customJsOpen}
        onClose={() => setModalState({ ...modalState, customJsOpen: false })}
        modalName="Custom JS & CSS"
      >
        <div style={{ height: "80vh", width: "100vh" }}>
          <CustomJsAndCssForScreen onClose={() => setModalState({ ...modalState, customJsOpen: false })} />
        </div>
      </BuilderModal>

      {/*Folder and Notes Modal */}
      <BuilderModal
        isOpen={modalState.notesOpen}
        onClose={() => setModalState({ ...modalState, notesOpen: false })}
        modalName="Add Folders and Tags"
      >
        <FolderTags tagValue={currentScreenData.folder_info_tag || []} onChange={handleNotesAndTag} />
        <NotesAndTags
          notesValue={currentScreenData.description || ""}
          tagValue={currentScreenData.info_tag || []}
          onChange={handleNotesAndTag}
        />
        <div className="mt_3">
          <Button onClick={handleFolderSave} variant="primary" size="small">
            Save
          </Button>
        </div>
      </BuilderModal>
    </>
  );
};

export const ScreenGenerationWithAI = () => {
  const { orgId } = useParams();
  const [payloadData, setPayloadData] = useState({
    user_request: "",
    sm_id: "",
    org_id: orgId,
    ai_model: "gemini"
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedScreenIndex, setSelectedScreenIndex] = useState(null);
  const { axiosInstance, handleError, handleSuccess } = useAxiosContext();
  const [responseScreenConfig, setResponseScreenConfig] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [workflowName, setWorkflowName] = useState("Preview");

  const createScreens = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.post("/ai/screens", payloadData);
      setResponseScreenConfig(data?.screens);
      handleSuccess("Screens generated successfully!");
      setSelectedScreenIndex(0);
    } catch (err) {
      console.error(err);
      setError("Failed to generate Screens. Please try again.");
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="screen-generation-container">
      <div className="h_100">
        <div>
          <StatemachineSelection
            selectedSM={payloadData.sm_id}
            onChange={(id) => setPayloadData({ ...payloadData, sm_id: id })}
            setWorkflowName={setWorkflowName}
          />
          <textarea
            id="userPrompt"
            value={payloadData.user_request}
            onChange={(e) => setPayloadData({ ...payloadData, user_request: e.target.value })}
            placeholder="Enter a custom prompt for AI to generate screen"
            rows="3"
            style={{ width: "100%", marginTop: "10px", padding: "8px", borderRadius: "4px" }}
          />
          <div className="d_flex gap_2" style={{ width: "fit-content", marginLeft: "auto" }}>
            <Button
              onClick={createScreens}
              isLoading={isLoading}
              variant={responseScreenConfig.length ? "secondary" : "primary"}
              disabled={!payloadData.sm_id || !payloadData.user_request}
            >
              {responseScreenConfig.length ? "Regenerate" : "Generate"}
            </Button>
            {responseScreenConfig?.length > 0 && (
              <Button
                onClick={() => setShowPreview(true)}
                variant="secondary"
                disabled={responseScreenConfig?.length === 0}
              >
                Preview
              </Button>
            )}
          </div>
        </div>

        {error && <ErrorMessage className="d_flex align_center h_100 justify_center">{error}</ErrorMessage>}

        <BuilderModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          modalName={`Screens for ${workflowName}`}
          bodyStyle={{ paddingTop: "0" }}
        >
          <ScreensPreview
            responseScreenConfig={responseScreenConfig}
            setSelectedScreenIndex={setSelectedScreenIndex}
            selectedScreenIndex={selectedScreenIndex}
          />
        </BuilderModal>
      </div>
    </div>
  );
};

export const StatemachineSelection = ({ selectedSM, onChange, label = "Select Workflow", setWorkflowName }) => {
  const { data, loading, error } = useFetchData("/state-machines");
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Something went wrong</div>;
  const handleChange = (obj) => {
    onChange(obj.id);
    setWorkflowName(obj?.name || "Selected Workflow");
  };
  return (
    <div className="builder_form">
      <SingleSelectBox
        selectedData={selectedSM}
        options={data}
        onChange={handleChange}
        label={label}
        placeholder={label}
      />
    </div>
  );
};

const ScreensPreview = ({ responseScreenConfig, setSelectedScreenIndex, selectedScreenIndex }) => {
  return (
    <div style={{ width: "70vw", height: "70vh", overflow: "auto" }}>
      {responseScreenConfig.length > 0 && (
        <div className="h_100 d_flex gap_2">
          {/* Left Sidebar */}
          <div
            style={{
              width: "250px",
              borderRight: "1px solid #e0e0e0",
              padding: "10px"
            }}
          >
            {responseScreenConfig.map((screen, index) => (
              <div
                key={index}
                onClick={() => setSelectedScreenIndex(index)}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  backgroundColor: selectedScreenIndex === index ? "#f0f0f0" : "transparent",
                  borderRadius: "4px",
                  marginBottom: "5px",
                  transition: "background-color 0.2s"
                }}
              >
                {screen.screenName}
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div style={{ flex: 1 }} className="overflow_scroll">
            {selectedScreenIndex !== null && (
              <div
                className="screen-preview-card"
                style={{
                  padding: "20px"
                }}
              >
                <h2
                  style={{
                    marginBottom: "15px",
                    color: "#333",
                    fontSize: "18px",
                    fontWeight: "500"
                  }}
                >
                  {responseScreenConfig[selectedScreenIndex].heading.name}
                </h2>

                {responseScreenConfig[selectedScreenIndex]?.button && (
                  <Button variant="primary" style={{ marginLeft: "0" }}>
                    {responseScreenConfig[selectedScreenIndex].button.name}
                  </Button>
                )}

                {responseScreenConfig[selectedScreenIndex]?.chart && (
                  <div style={{ marginTop: "10px" }}>
                    <ReactApexChart
                      options={formatChartData(responseScreenConfig[selectedScreenIndex].chart[0]).options}
                      series={formatChartData(responseScreenConfig[selectedScreenIndex].chart[0]).series}
                      type={responseScreenConfig[selectedScreenIndex].chart[0].type}
                      height={350}
                      style={{ maxWidth: "700px", margin: "0 auto" }}
                    />
                  </div>
                )}

                {responseScreenConfig[selectedScreenIndex]?.table && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px"
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        overflow: "hidden",
                        borderRadius: "0"
                      }}
                    >
                      <thead>
                        <tr>
                          {responseScreenConfig[selectedScreenIndex].table.columns.map((column, idx) => (
                            <th
                              key={idx}
                              style={{
                                padding: "12px",
                                backgroundColor: "#f8f9fa",
                                textAlign: "left"
                              }}
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {responseScreenConfig[selectedScreenIndex].table.data?.map((row, idx) => (
                          <tr
                            key={idx}
                            style={{
                              borderBottom: "1px solid #dee2e6"
                            }}
                          >
                            {responseScreenConfig[selectedScreenIndex].table.columns.map((column, colIdx) => (
                              <td
                                key={colIdx}
                                style={{
                                  padding: "12px",
                                  color: "#212529"
                                }}
                              >
                                {row[column]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenRenderer;
